import type { SupabaseClient } from '@supabase/supabase-js'
import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import { RouteToolError, type RouteToolsRepository } from '../../application/RouteToolsRepository'

const runStatus = z.enum([
  'RUNNING',
  'REVIEW',
  'APPLIED',
  'PARTIALLY_APPLIED',
  'FAILED',
  'CANCELLED',
  'REVERTED',
])
const tool = z.enum(['OPTIMIZATION', 'STREET_SNAP', 'AI_ASSIST'])
const mode = z.enum(['ORDER', 'BALANCE', 'FLAGS'])
const nullableNumber = z
  .union([z.number(), z.string()])
  .nullable()
  .transform((value) => (value === null ? null : Number(value)))
const flagProposal = z.object({
  locationId: z.string(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  latitude: nullableNumber,
  longitude: nullableNumber,
  before: z.object({
    latitude: nullableNumber.default(null),
    longitude: nullableNumber.default(null),
    source: z.enum(['GEOMETRY', 'MANUAL', 'AI', 'MAPBOX']).nullable().default(null),
    version: z.number().int().default(0),
  }),
  proposedLatitude: nullableNumber,
  proposedLongitude: nullableNumber,
  abstainReason: z.string().nullable(),
  warnings: z.array(z.string()).default([]),
  details: z
    .object({
      streetName: z.string().optional(),
      roadClass: z.string().nullable().optional(),
      axisDistance: z.number().optional(),
      setback: z.number().optional(),
      moveDistance: z.number().optional(),
    })
    .loose()
    .nullable(),
  status: z.enum(['PROPOSED', 'ABSTAINED', 'PROTECTED', 'APPLIED', 'CONFLICT', 'REVERTED']),
  changedSinceRun: z.boolean(),
})
const flagLocation = z.object({
  id: z.string(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  latitude: nullableNumber,
  longitude: nullableNumber,
  flagSource: z.string().nullable(),
  flagVersion: z.number().int(),
})
const segment = z.object({
  id: z.string(),
  name: z.string().nullable(),
  roadClass: z.string().nullable(),
  points: z.array(z.tuple([z.number(), z.number()])),
})
const usage = z
  .object({
    provider: z.string().optional(),
    calls: z.number().optional(),
    billable: z.boolean().optional(),
    shipments: z.number().optional(),
    vehicles: z.number().optional(),
  })
  .nullable()
const metrics = z.object({
  stops: z.number(),
  travelSeconds: z.number(),
  distanceMeters: z.number(),
  visitSeconds: z.number(),
  totalSeconds: z.number(),
})
const conflict = z.object({
  routeId: z.string().optional(),
  locationId: z.string().optional(),
  reason: z.string(),
})

const listItem = z.object({
  id: z.string(),
  tool,
  mode,
  status: runStatus,
  season: z.string(),
  routeIds: z.array(z.string()),
  routeNames: z.array(z.string()),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  proposalCount: z.number().int(),
  appliedCount: z.number().int(),
  errorCode: z.string().nullable(),
  usage,
})

const detail = z.object({
  id: z.string(),
  tool,
  mode,
  status: runStatus,
  season: z.string(),
  parameters: z
    .object({
      serviceMinutes: z.number().optional(),
      lockedLocationIds: z.array(z.string()).optional(),
      depot: z.object({ lat: z.number(), lng: z.number() }).nullable().optional(),
      includeUnassigned: z.boolean().optional(),
      setbackMeters: z.number().optional(),
      method: z.enum(['GEOMETRY', 'MAPBOX_ACCESS_POINT', 'GEMINI_VISION']).optional(),
      model: z.string().optional(),
      objective: z.enum(['DISTANCE', 'TIME']).optional(),
      travelDurationMultiple: z.number().optional(),
      avoidHighways: z.boolean().optional(),
      balance: z.enum(['NONE', 'LIGHT', 'STRONG']).nullable().optional(),
    })
    .loose(),
  summary: z
    .object({
      excluded: z
        .array(z.object({ locationId: z.string(), routeId: z.string(), reason: z.string() }))
        .default([]),
      moved: z
        .array(z.object({ locationId: z.string(), fromRouteId: z.string(), toRouteId: z.string() }))
        .default([]),
      totals: z
        .object({
          before: z.object({
            travelSeconds: z.number(),
            distanceMeters: z.number(),
            totalSeconds: z.number(),
          }),
          after: z.object({
            travelSeconds: z.number(),
            distanceMeters: z.number(),
            totalSeconds: z.number(),
          }),
        })
        .nullable()
        .default(null),
      balanceTargetSeconds: z.number().nullable().default(null),
      unassignedIncluded: z.number().default(0),
      unassignedPlaced: z.number().default(0),
    })
    .nullable(),
  usage,
  errorCode: z.string().nullable(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  createdByName: z.string().nullable(),
  proposals: z.array(
    z.object({
      routeId: z.string(),
      routeName: z.string(),
      routeColor: z.string(),
      beforeIds: z.array(z.string()),
      afterIds: z.array(z.string()),
      metrics: z.object({ before: metrics, after: metrics }).nullable(),
      status: z.enum(['PROPOSED', 'APPLIED', 'CONFLICT', 'REVERTED']),
      changedSinceRun: z.boolean(),
    }),
  ),
  locationProposals: z.array(flagProposal).default([]),
  applications: z.array(
    z.object({
      id: z.string(),
      appliedAt: z.string(),
      report: z.object({ applied: z.array(z.string()), conflicts: z.array(conflict) }),
      revertedAt: z.string().nullable(),
      revertReport: z.object({ restored: z.array(z.string()), conflicts: z.array(conflict) }).nullable(),
    }),
  ),
})

const optimizeResult = z.object({
  runId: z.string(),
  status: runStatus,
  error: z.string().optional(),
})

const applyResult = z.object({
  status: z.enum(['APPLIED', 'PARTIAL', 'CONFLICT']),
  applicationId: z.string().nullable().optional(),
  applied: z.array(z.string()),
  conflicts: z.array(conflict),
})

const revertResult = z.object({
  status: z.enum(['REVERTED', 'PARTIAL', 'CONFLICT']),
  restored: z.array(z.string()),
  conflicts: z.array(conflict),
})

// ---------------------------------------------------------------- Drapeaux ajustés par l'IA (lot E)

const counts = z.object({
  total: z.number().int(),
  pending: z.number().int(),
  running: z.number().int(),
  proposed: z.number().int(),
  reviewNeeded: z.number().int(),
  error: z.number().int(),
  excluded: z.number().int(),
})

const aiSettings = z.object({
  provider: z.literal('GEMINI_API'),
  model: z.string().nullable(),
  modelVerifiedAt: z.string().nullable(),
  concurrency: z.number().int(),
  maxAttempts: z.number().int(),
  maxTasksPerRun: z.number().int(),
  reviewMoveThresholdM: z.union([z.number(), z.string()]).transform(Number),
  priceInputPerMtok: nullableNumber,
  priceOutputPerMtok: nullableNumber,
  priceCheckedOn: z.string().nullable(),
  imagerySource: z.enum(['QC_ORTHO_LAURENTIDES_2022']).nullable(),
  pilotRoutes: z.array(z.object({ id: z.string(), name: z.string(), season: z.string(), color: z.string() })),
  updatedAt: z.string().nullable(),
})

const tokenUsage = z
  .object({
    calls: z.number().optional(),
    promptTokens: z.number().optional(),
    outputTokens: z.number().optional(),
    totalTokens: z.number().optional(),
  })
  .loose()

const aiTask = z.object({
  taskId: z.string(),
  locationId: z.string(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  status: z.enum(['PENDING', 'RUNNING', 'PROPOSED', 'REVIEW_NEEDED', 'ERROR', 'EXCLUDED']),
  exclusionReason: z.enum(['UNLOCATED', 'MANUAL_PROTECTED', 'STOPPED']).nullable(),
  errorCode: z.string().nullable(),
  attempts: z.number().int(),
  durationMs: z.number().nullable(),
  usage: tokenUsage,
  imagery: z
    .object({
      source: z.string().optional(),
      date: z.string().optional(),
      attribution: z.string().optional(),
    })
    .loose()
    .nullable(),
  latitude: nullableNumber,
  longitude: nullableNumber,
  flagBefore: z
    .object({
      latitude: nullableNumber.default(null),
      longitude: nullableNumber.default(null),
      source: z.string().nullable().default(null),
      version: z.number().int().default(0),
    })
    .nullable(),
  stillMember: z.boolean(),
  currentFlagVersion: z.number().int(),
  proposal: z
    .object({
      status: z.enum(['PROPOSED', 'ABSTAINED', 'PROTECTED', 'APPLIED', 'CONFLICT', 'REVERTED']),
      proposedLatitude: nullableNumber,
      proposedLongitude: nullableNumber,
      abstainReason: z.string().nullable(),
      warnings: z.array(z.string()).default([]),
      details: z
        .object({
          reason: z.string().optional(),
          ambiguities: z.array(z.string()).optional(),
          moveDistance: z.number().optional(),
          positionDistance: z.number().optional(),
          model: z.string().optional(),
          promptVersion: z.string().optional(),
          imagerySource: z.string().optional(),
        })
        .loose()
        .nullable(),
      reviewDecision: z.enum(['ACCEPTED', 'CORRECTED', 'IGNORED']).nullable(),
      correctedLatitude: nullableNumber,
      correctedLongitude: nullableNumber,
      changedSinceRun: z.boolean(),
    })
    .nullable(),
})

const aiRun = z.object({
  id: z.string(),
  status: runStatus,
  season: z.string(),
  routeId: z.string(),
  routeName: z.string().nullable(),
  routeColor: z.string().nullable(),
  parameters: z
    .object({
      model: z.string().optional(),
      imagerySource: z.string().optional(),
      concurrency: z.number().optional(),
    })
    .loose(),
  usage: tokenUsage.extend({ provider: z.string().optional(), billable: z.boolean().optional() }).nullable(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  createdByName: z.string().nullable(),
  counts,
  tasks: z.array(aiTask),
  applications: z.array(
    z.object({
      id: z.string(),
      appliedAt: z.string(),
      report: z.object({ applied: z.array(z.string()), conflicts: z.array(conflict) }),
      revertedAt: z.string().nullable(),
      revertReport: z.object({ restored: z.array(z.string()), conflicts: z.array(conflict) }).nullable(),
    }),
  ),
})

/** Code `ROUTE_…` d'une erreur RPC ; sinon un code générique, jamais le message brut. */
function rpcErrorCode(message: string | undefined): string {
  return /[A-Z][A-Z0-9_]{3,}/.exec(message ?? '')?.[0] ?? 'UNEXPECTED'
}

/** Corps d'erreur `{ error: 'CODE' }` renvoyé par la fonction Edge. */
async function functionErrorCode(error: unknown): Promise<string> {
  const context = (error as { context?: unknown } | null)?.context
  if (context instanceof Response) {
    const body = (await context.json().catch(() => null)) as { error?: unknown } | null
    if (typeof body?.error === 'string') return body.error
  }
  return 'UNEXPECTED'
}

export function createSupabaseRouteTools(client: AppSupabaseClient): RouteToolsRepository {
  const rpcClient = client as unknown as SupabaseClient

  /** `command` nul : fonction sans argument (l'API ne la trouverait pas avec un `p_command`). */
  async function rpc<T>(name: string, command: object | null, schema: z.ZodType<T>): Promise<T> {
    let response: { data: unknown; error: { message?: string } | null }
    try {
      response = (await (command === null
        ? rpcClient.rpc(name)
        : rpcClient.rpc(name, { p_command: command }))) as typeof response
    } catch {
      throw new RouteToolError('NETWORK')
    }
    if (response.error) throw new RouteToolError(rpcErrorCode(response.error.message))
    const parsed = schema.safeParse(response.data)
    if (!parsed.success) throw new RouteToolError('RESPONSE_INVALID')
    return parsed.data
  }

  async function invoke<T>(body: object, schema: z.ZodType<T>, name = 'route-optimization'): Promise<T> {
    let invocation: Awaited<ReturnType<typeof client.functions.invoke>>
    try {
      invocation = await client.functions.invoke(name, { body })
    } catch {
      throw new RouteToolError('NETWORK')
    }
    if (invocation.error) throw new RouteToolError(await functionErrorCode(invocation.error))
    const parsed = schema.safeParse(invocation.data)
    if (!parsed.success) throw new RouteToolError('RESPONSE_INVALID')
    return parsed.data
  }

  return {
    getOptimizationStatus: () => invoke({ action: 'status' }, z.object({ configured: z.boolean() })),
    async optimize(command) {
      const result = await invoke({ action: 'optimize', ...command }, optimizeResult)
      return { runId: result.runId, status: result.status, error: result.error ?? null }
    },
    listRuns: (season) => rpc('route_tool_runs_v1', { season }, z.array(listItem)),
    getRun: (runId) => rpc('route_tool_run_v1', { runId }, detail),
    async apply(command) {
      const result = await rpc('apply_route_tool_proposals_v1', command, applyResult)
      return { ...result, applicationId: result.applicationId ?? null }
    },
    revert: (command) => rpc('revert_route_tool_application_v1', command, revertResult),
    async cancel(runId) {
      await rpc('cancel_route_tool_run_v1', { runId }, z.object({ status: z.string() }))
    },
    async getStreetSegmentsStatus() {
      const status = await rpc(
        'street_segments_status_v1',
        null,
        z.object({
          count: z.number(),
          sourceVersion: z.string().nullable(),
          sourceDate: z.string().nullable(),
        }),
      )
      return status
    },
    getStreetSegmentsNear: (locationIds) => rpc('street_segments_near_v1', { locationIds }, z.array(segment)),
    createFlagRun: (command) =>
      rpc(
        'create_flag_run_v1',
        command,
        z.object({ runId: z.string(), replayed: z.boolean(), locations: z.array(flagLocation) }),
      ),
    async completeFlagRun(command) {
      await rpc('complete_flag_run_v1', { ...command, status: 'REVIEW' }, z.object({ status: z.string() }))
    },
    async applyFlags(command) {
      const result = await rpc('apply_flag_proposals_v1', command, applyResult)
      return { ...result, applied: result.applied, applicationId: result.applicationId ?? null }
    },
    revertFlags: (command) => rpc('revert_flag_application_v1', command, revertResult),

    getAiSettings: () => rpc('route_ai_settings_v1', null, aiSettings),
    saveAiSettings: (command) => rpc('set_route_ai_settings_v1', command, aiSettings),
    getAiProviderStatus: () =>
      invoke({ action: 'status' }, z.object({ keyConfigured: z.boolean() }), 'route-flags-ai'),
    async verifyAiModel() {
      const result = await invoke(
        { action: 'verify-model' },
        z.object({
          verified: z.boolean(),
          model: z.string().optional(),
          displayName: z.string().nullable().optional(),
          inputTokenLimit: z.number().nullable().optional(),
          error: z.string().optional(),
          detail: z
            .object({
              step: z.string(),
              httpStatus: z.number(),
              providerStatus: z.string().nullable(),
              message: z.string().nullable(),
            })
            .optional(),
        }),
        'route-flags-ai',
      )
      return result.verified && result.model
        ? {
            verified: true,
            model: result.model,
            displayName: result.displayName ?? null,
            inputTokenLimit: result.inputTokenLimit ?? null,
          }
        : { verified: false, error: result.error ?? 'UNEXPECTED', detail: result.detail ?? null }
    },
    createAiFlagRun: (command) =>
      rpc('create_ai_flag_run_v1', command, z.object({ runId: z.string(), replayed: z.boolean() })),
    async processAiFlagRun(runId) {
      const result = await invoke(
        { action: 'process', runId },
        z.object({
          runStatus: z.string(),
          processed: z.number().int(),
          counts: counts.nullable().optional(),
        }),
        'route-flags-ai',
      )
      return { runStatus: result.runStatus, processed: result.processed, counts: result.counts ?? null }
    },
    getAiFlagRun: (runId) => rpc('ai_flag_run_v1', { runId }, aiRun),
    getActiveAiFlagRun: (routeId) => rpc('active_ai_flag_run_v1', { routeId }, aiRun.nullable()),
    async stopAiFlagRun(runId) {
      await rpc('stop_ai_flag_tasks_v1', { runId }, z.object({ runId: z.string() }))
    },
    async resumeAiFlagRun(runId, scope) {
      await rpc('resume_ai_flag_run_v1', { runId, scope }, z.object({ runId: z.string() }))
    },
    async setFlagDecision(command) {
      await rpc('set_flag_proposal_decision_v1', command, z.object({ runId: z.string() }))
    },
  }
}
